/**
 * Un KB de «Lo que sabemos» — GEN-EKB-019: su lista de iKus y el detalle de cada iKu con sus versiones.
 * Reutiliza los endpoints de la 009 (listIkus, getIku). Sin mockup: libro de estilo (§2), misma tarjeta y tipografía.
 * Si el kernel no deja abrirlo (p. ej. un KB del mapa que el Champion no tiene concedido) se dice «No disponible».
 */
import { useEffect, useState } from 'react'
import { desenlazarIku, editarIku, getIku, listIkus, retirarKb } from '../api/client.js'
import { FormIku, KbsDelIku } from './IkusPanel.jsx'
import { ModalEditarKb, mensaje } from './SaberAltas.jsx'

const ESTADO = { vigente: 'Vigente', retirado: 'Retirado', propuesto: 'Propuesto' }
const ETIQUETA = { confidencial: 'Confidencial', interna: 'Interna', publica: 'Pública' }
const CLASE = { confidencial: 'conf', interna: 'int', publica: 'pub' }
const dia = iso => (iso ? new Date(/^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso}T00:00:00` : iso).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—')

function useCarga(fn, clave) {
  const [estado, setEstado] = useState({ cargando: true })
  useEffect(() => {
    let vivo = true
    setEstado({ cargando: true })
    fn().then(datos => vivo && setEstado({ datos })).catch(() => vivo && setEstado({ noDisponible: true }))
    return () => { vivo = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clave])
  return estado
}

function NoDisponible() {
  return <p className="dk-saber__aviso" role="alert">No disponible</p>
}

// GEN-PX-019: `gestion` es el KB tal como lo devuelve listKbs (con `puede_gestionar`); sin él no hay acciones.
export function ListaIkus({ kb, onAbrirIku, onVolver, gestion = null, onCambioKb }) {
  const [editando, setEditando] = useState(false)
  const [aviso, setAviso] = useState(null)
  const puede = Boolean(gestion?.puede_gestionar) && gestion.estado === 'activo'

  async function retirar() {
    if (!window.confirm(`Retirar KB · Bloque de conocimiento\n\n¿Retirar el KB «${kb.nombre}»? No se borra: deja de aparecer como activo.`)) return
    setAviso(null)
    try {
      const r = await retirarKb(kb.id)
      await onCambioKb?.({ retirado: true, pendiente: r?.retirada?.resultado === 'pendiente' ? r.retirada.ikus_afectados : null, nombre: kb.nombre })
    } catch (err) {
      setAviso(mensaje(err))
    }
  }

  const [recarga, setRecarga] = useState(0)
  const { cargando, datos, noDisponible } = useCarga(() => listIkus(kb.id, 'todos'), `${kb.id}:${recarga}`)
  // GEN-EKB-043: quitar el enlace no retira el iKu, que sigue en su KB de origen
  async function quitarEnlace(i) {
    setAviso(null)
    try { await desenlazarIku(i.id, kb.id); setRecarga(n => n + 1) } catch (err) { setAviso(mensaje(err)) }
  }
  const ikus = [...(datos?.ikus ?? [])].sort((a, b) => (a.estado === 'vigente' ? 0 : 1) - (b.estado === 'vigente' ? 0 : 1))
  return (
    <section className="dk-mapa" aria-label={`iKus de ${kb.nombre}`}>
      <button type="button" className="dk-mapa__volver" onClick={onVolver}>← Lo que sabemos</button>
      <div className="dk-rotulo">KB · Bloque de conocimiento</div>
      <div className="dk-mapa__cab">
        <h2>{kb.nombre}</h2>
        {puede && (
          <div className="dk-mapa__acts">
            <button type="button" className="dk-btn" onClick={() => setEditando(true)}>Editar KB</button>
            <button type="button" className="dk-btn" onClick={retirar}>Retirar KB</button>
          </div>
        )}
      </div>
      {aviso && <p className="dk-saber__aviso" role="alert">{aviso}</p>}
      {editando && <ModalEditarKb kb={gestion} onCerrar={() => setEditando(false)} onGuardado={async nuevo => { setEditando(false); await onCambioKb?.({ kb: nuevo }) }} />}
      {cargando && <p className="dk-saber__aviso">Cargando…</p>}
      {noDisponible && <NoDisponible />}
      {datos && ikus.length === 0 && <p className="dk-saber__aviso">Este KB aún no tiene iKus.</p>}
      {ikus.length > 0 && (
        <div className="dk-area dk-ikus">
          <div className="dk-ikus__fila dk-ikus__fila--cab" aria-hidden="true">
            <span>Título</span><span>Tipo</span><span>Vigencia</span><span>Versión</span><span>Estado</span>
          </div>
          {ikus.map(i => (
            <div key={i.id}>
              <button type="button" className={`dk-ikus__fila${i.estado === 'vigente' ? '' : ' dk-ikus__fila--retirado'}`} onClick={() => onAbrirIku(i.id)}>
                <span className="dk-ikus__titulo">{i.titulo ?? '(ficha ilegible)'}</span>
                <span className="dk-ikus__tipo">{i.tipo_etiqueta ?? '—'}</span>
                <span>{dia(i.vigencia)}</span>
                <span>v{i.version}</span>
                <span>{ESTADO[i.estado] ?? i.estado}</span>
              </button>
              {i.enlazado && (
                <p className="dk-ikus__enlazado">
                  <span className="dk-ikus__marca">Enlazado</span>
                  {i.origen_kb && <> <a href={`#/saber/kb/${i.origen_kb.id}`} aria-label={`KB de origen: ${i.origen_kb.nombre}`}>de «{i.origen_kb.nombre}»</a></>}
                  {' '}<button type="button" className="dk-btn" aria-label={`Quitar el enlace de ${i.titulo ?? 'este iKu'}`} onClick={() => quitarEnlace(i)}>Quitar enlace</button>
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

export function DetalleIku({ kb, ikuId, onVolver, puedeGestionar = false, onCambio }) {
  const [recarga, setRecarga] = useState(0)
  const [editando, setEditando] = useState(false)
  const [tipos, setTipos] = useState([])
  const { cargando, datos, noDisponible } = useCarga(() => getIku(ikuId), `${ikuId}:${recarga}`)
  const iku = datos?.iku
  const gestionable = puedeGestionar && iku?.estado === 'vigente'

  // Los tipos de iKu llegan del kernel con la lista del KB; solo hacen falta para editar.
  useEffect(() => {
    if (!gestionable || !kb?.id) return
    let vivo = true
    listIkus(kb.id, 'vigente').then(r => vivo && setTipos(r.tipos ?? [])).catch(() => {})
    return () => { vivo = false }
  }, [gestionable, kb?.id])

  async function cambio() {
    setRecarga(n => n + 1)
    await onCambio?.()
  }
  const anteriores = (iku?.versiones ?? []).filter(v => v.version < iku.version).sort((a, b) => b.version - a.version)
  return (
    <section className="dk-mapa" aria-label="Detalle del iKu">
      <button type="button" className="dk-mapa__volver" onClick={onVolver}>← {kb.nombre}</button>
      {cargando && <p className="dk-saber__aviso">Cargando…</p>}
      {noDisponible && <NoDisponible />}
      {iku && (
        <div className="dk-area dk-iku">
          <div className="dk-rotulo">{editando ? 'Editar iKu · Unidad incremental de conocimiento' : 'iKu · Unidad incremental de conocimiento'}</div>
          <div className="dk-iku__cab">
            <h2>{iku.titulo}</h2>
            <span className={`dk-et dk-et--${CLASE[iku.etiqueta] ?? 'int'}`}>{ETIQUETA[iku.etiqueta] ?? iku.etiqueta}</span>
          </div>
          <p className="dk-iku__meta">
            {iku.tipo_etiqueta} · versión {iku.version} · {ESTADO[iku.estado] ?? iku.estado}
            {iku.vigencia ? ` · vigencia ${dia(iku.vigencia)}` : ''}
          </p>
          {iku.resumen && <p className="dk-iku__resumen">{iku.resumen}</p>}
          {editando ? (
            <FormIku
              inicial={iku}
              tipos={tipos}
              etiquetaBoton="Guardar (nueva versión)"
              onCancelar={() => setEditando(false)}
              onGuardar={async f => {
                const { kb_id: _kb, ...cambios } = f
                await editarIku(iku.id, cambios)
                setEditando(false)
                await cambio()
              }}
            />
          ) : (
            <pre className="dk-iku__texto">{iku.texto}</pre>
          )}
          {/* GEN-PX-019: solo si el kernel dice que puedo gestionar el KB (R-PT-04); el kernel revalida cada llamada. */}
          {gestionable && !editando && <button type="button" className="dk-btn dk-btn--primario" onClick={() => setEditando(true)}>Editar iKu</button>}
          <dl className="dk-iku__datos">
            <dt>Origen</dt><dd>{iku.origen || '—'}</dd>
          </dl>
          <h3 className="dk-iku__sub">Versiones anteriores</h3>
          {anteriores.length === 0 && <p className="dk-iku__meta">Sin versiones anteriores.</p>}
          <ul className="dk-iku__versiones">
            {anteriores.map(v => <li key={v.version}><b>v{v.version}</b> · {dia(v.creado_en)} · {v.autor}</li>)}
          </ul>
          {gestionable && !editando && <KbsDelIku iku={iku} onCambio={cambio} />}
        </div>
      )}
    </section>
  )
}
